import { LuFlower } from "react-icons/lu";
import { Gi3dHammer } from "react-icons/gi";
import { HiMiniPaintBrush } from "react-icons/hi2";
import { motion } from "framer-motion";
import { useRef } from "react";
import { useInView } from "framer-motion";

import Forgia from "../assets/img/forgia.jpg"

export default function ContentHero() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" }
    }
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { 
        duration: 0.5,
        delay: i * 0.2
      }
    })
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95, rotate: -2 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: { 
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div ref={ref} className="relative isolate overflow-hidden bg-white px-6 py-24 sm:py-32 lg:overflow-visible lg:px-0">
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-y-10"
      >
        <motion.div 
          className="lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8"
          variants={itemVariants}
        >
          <div className="lg:pr-4">
            <div className="lg:max-w-lg">
              <motion.p 
                className="text-base font-semibold text-green-600"
                variants={itemVariants}
              >
                Arte del Ferro Battuto
              </motion.p>
              <motion.h1 
                className="mt-2 text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl"
                variants={itemVariants}
              >
                L'Arte di Saverio
              </motion.h1>
              <motion.p 
                className="mt-6 text-xl text-gray-700"
                variants={itemVariants}
              >
                Fiorellini, punte, virgole, corni, tondeggi fluttuanti di ferro battuto sono stati i giochi dei suoi 8 anni,
                la magia della sua infanzia, una formazione che l'ha portato oggi a creare prati di rose, armoniose lampade,
                e tanto altro ancora.
              </motion.p>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          className="sm:-ml-12 -mt-12 p-4 sm:p-12 lg:sticky lg:top-8 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:overflow-hidden"
          variants={imageVariants}
        >
          <motion.div 
            className="relative overflow-hidden rounded-xl"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.4 }}
          >
            <motion.img
              alt="Saverio al lavoro nella forgia"
              src={Forgia}
              className="w-full max-w-none rounded-xl bg-gray-900 shadow-xl ring-1 ring-gray-400/10 lg:w-[57rem]"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.5 }}
            />
            <motion.div 
              className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"
              whileHover={{ opacity: 1 }}
            />
          </motion.div>
        </motion.div>
        
        <motion.div 
          className="lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8"
          variants={itemVariants}
        >
          <div className="lg:pr-4">
            <div className="max-w-xl text-gray-700 lg:max-w-lg">
              <motion.p variants={itemVariants}>
                Saverio è un abilissimo fabbro e un artista sofisticato che plasma il ferro in opere complesse e raffinate.
                A volte coadiuva i suoi lavori con materiali diversi per raccontare la natura e l'ambiente da cui proveniamo,
                unendo arte e rispetto per il mondo naturale.
              </motion.p>
              
              <motion.ul 
                role="list" 
                className="mt-8 space-y-8 text-gray-600"
                variants={containerVariants}
              >
                <motion.li 
                  className="flex gap-x-3"
                  custom={0}
                  variants={featureVariants}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6, type: "spring", stiffness: 260, damping: 20 }}
                    className="flex-none mt-1 bg-green-50 p-1 rounded-full"
                  >
                    <LuFlower aria-hidden="true" className="h-5 w-5 text-green-600" />
                  </motion.div>
                  <span>
                    <strong className="font-semibold text-gray-900">Natura come musa.</strong> Saverio trova ispirazione nella bellezza naturale che lo circonda, plasmando il ferro in forme che evocano la flora dei Castelli Romani.
                  </span>
                </motion.li>
                
                <motion.li 
                  className="flex gap-x-3"
                  custom={1}
                  variants={featureVariants}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8, type: "spring", stiffness: 260, damping: 20 }}
                    className="flex-none mt-1 bg-green-50 p-1 rounded-full"
                  >
                    <Gi3dHammer aria-hidden="true" className="h-5 w-5 text-green-600" />
                  </motion.div>
                  <span>
                    <strong className="font-semibold text-gray-900">Artigianato d'eccellenza.</strong> Utilizzando tecniche tradizionali, crea pezzi che combinano utilità e bellezza, adatti anche per location di lusso.
                  </span>
                </motion.li>
                
                <motion.li 
                  className="flex gap-x-3"
                  custom={2}
                  variants={featureVariants}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
                    className="flex-none mt-1 bg-green-50 p-1 rounded-full"
                  >
                    <HiMiniPaintBrush aria-hidden="true" className="h-5 w-5 text-green-600" />
                  </motion.div>
                  <span>
                    <strong className="font-semibold text-gray-900">Arte ed espressione.</strong> Ogni pezzo racconta una storia, con elementi che omaggiano la storia e l'arte dei suoi avi, mescolando tradizione e modernità.
                  </span>
                </motion.li>
              </motion.ul>
              
              <motion.p 
                className="mt-8"
                variants={itemVariants}
              >
                Le emozioni prendono forma dalla sensibilità e dalle mani di Saverio, che crea oggetti unici in ferro battuto
                dalle forme grandiose, ideali per chi cerca un tocco di esclusività e arte in casa o negli spazi di lusso.
              </motion.p>
              
              <motion.div 
                variants={containerVariants}
                className="mt-16"
              >
                <motion.h2 
                  className="text-2xl font-bold tracking-tight text-gray-900"
                  variants={itemVariants}
                >
                  Un'arte senza confini.
                </motion.h2>
                <motion.p 
                  className="mt-6"
                  variants={itemVariants}
                >
                  Immerso nella natura dei Castelli Romani, Saverio combina materiali e ispirazioni per opere che onorano la
                  bellezza del passato e l'innovazione moderna, sempre con un occhio al rispetto dell'ambiente.
                </motion.p>
              </motion.div>
              
              <motion.div
                className="mt-8 inline-flex"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.5 }}
              >
                <motion.a
                  href="/store"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#406580] hover:bg-[#305570] shadow-sm transition-colors"
                >
                  Scopri le creazioni
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 ml-2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </motion.a>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
      
      {/* Background decorative elements */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 1.5, delay: 0.5 }}
        className="absolute top-0 right-0 -z-10 h-full w-1/4 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-green-200 to-transparent opacity-20"
      />
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 1.5, delay: 0.8 }}
        className="absolute bottom-0 left-0 -z-10 h-1/3 w-full bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-[#406580]/30 to-transparent opacity-20"
      />
    </div>
  );
}